import { usePatientFlags, useFlags } from "@/lib/queries";
import { useAttachPatientFlag, useRemovePatientFlag } from "@/lib/mutations";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { swatchBg } from "@emr/shared/flags";

const ADMIN_FLAG_COLOR = "bg-purple-100 text-purple-800 border-purple-300";
const FALLBACK_COLOR = "bg-red-100 text-red-800 border-red-300";

export function PatientFlagsPanel({ patientId }: { patientId: string }) {
  const { data: patientFlagsData } = usePatientFlags(patientId);
  const { data: flagsData } = useFlags();
  const attach = useAttachPatientFlag(patientId);
  const remove = useRemovePatientFlag(patientId);
  const { isAdmin, permissions } = useAuth();
  const canAttach = isAdmin || permissions.has("patients.create") || permissions.has("patients.edit") || permissions.has("appointments.create") || permissions.has("appointments.edit") || permissions.has("flags.create");
  const canRemove = isAdmin || permissions.has("flags.delete");

  const patientFlags = patientFlagsData?.rows ?? [];
  const catalog = Array.isArray(flagsData) ? flagsData : (flagsData as any)?.rows ?? [];

  const onAdd = (flagId: string) => attach.mutate(flagId);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {patientFlags.length === 0 && <span className="text-sm text-muted">No flags.</span>}
        {/* Attached flag badges — always honour the flag's colour; never skip. */}
        {patientFlags.map((pf) => {
          const flag = catalog.find((f: any) => f.id === pf.flagId);
          const color = flag?.kind === "admin" ? ADMIN_FLAG_COLOR : (flag?.color || FALLBACK_COLOR);
          // Only admins can remove admin-flag attachments; others can remove their own user-flag attachments.
          const canRemoveThis = flag?.kind === "admin" ? isAdmin : canRemove;
          return (
            <Badge key={pf.id} variant={null} className={cn("flex items-center gap-1 border", color)}>
              {flag?.name ?? "Flag"}
              {canRemoveThis && (
                <button onClick={() => remove.mutate(pf.flagId)} className="ml-1 rounded-full hover:bg-black/10"><X size={12} /></button>
              )}
            </Badge>
          );
        })}
      </div>
      {/* Dropdown — only flags not yet attached are listed; each option shows a colour swatch. */}
      {canAttach && catalog.length > 0 && (
        <Select onValueChange={onAdd}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Add flag…" /></SelectTrigger>
          <SelectContent>
            {catalog
              .filter((f: any) => !patientFlags.some((pf) => pf.flagId === f.id))
              .map((f: any) => {
                const color = f.kind === "admin" ? ADMIN_FLAG_COLOR : (f.color || FALLBACK_COLOR);
                return (
                  <SelectItem key={f.id} value={f.id}>
                    <span className="flex items-center gap-2">
                      <span className={cn("inline-block h-3 w-3 rounded-full border border-black/10", swatchBg(color))} />
                      {f.name}
                      {f.kind === "admin" && <span className="text-[10px] text-purple-700">(admin)</span>}
                    </span>
                  </SelectItem>
                );
              })}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
